import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { authService } from "../../services/authService";
import { Colors } from "../../constants/colors";
import { PYTHON_BACKEND_URL } from "../../constants/backend";
import { getResolvedPythonBackendUrl } from "../../services/backendUrlResolver";
import { formatFileSize } from "../../utils/formatFileSize";
import { AdminDatabaseIcon as DatabaseIcon, AdminRefreshIcon as RefreshIcon, AdminTrashIcon as TrashIcon, AdminCloseIcon as CloseIcon, AdminCheckIcon as CheckIcon, AdminXCircleIcon as XCircleIcon, } from "../../icons";
import { clearExcelLiveTracker, clearPptLiveTracker, fetchExcelLiveTrackerStats, fetchPptLiveTrackerStats, } from "./manageDataStorage";
const CLEAR_OPTION_LABELS = {
    pptLiveTracker: "PowerPoint live tracker",
    excelLiveTracker: "Excel live tracker",
    pythonDecks: "Presentation analysis data",
    externalRag: "Uploaded file search data",
    plans: "Builder plans",
    conversations: "Saved conversations",
};
const Checkbox = ({ checked, onChange, disabled }) => (_jsx("button", { type: "button", onClick: () => !disabled && onChange(!checked), disabled: disabled, style: {
        width: 20,
        height: 20,
        borderRadius: 4,
        border: `2px solid ${checked ? Colors.PrimaryPurple60 : Colors.Gray400}`,
        backgroundColor: checked ? Colors.PrimaryPurple60 : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        transition: "all 0.15s ease",
        opacity: disabled ? 0.5 : 1,
    }, children: checked && _jsx(CheckIcon, { className: "text-white" }) }));
const DbCard = ({ title, checked, onCheckedChange, children, description, clearEffect, disabled }) => (_jsxs("div", { style: {
        border: `1px solid ${checked ? Colors.PrimaryPurple30 : Colors.Gray300}`,
        borderRadius: 12,
        padding: 16,
        backgroundColor: checked ? Colors.PrimaryPurple05 : Colors.Gray100,
        transition: "all 0.2s ease",
    }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }, children: [_jsx(Checkbox, { checked: checked, onChange: onCheckedChange, disabled: disabled }), _jsx("span", { style: { fontSize: 14, fontWeight: 600, color: Colors.Gray900 }, children: title })] }), _jsxs("div", { style: { paddingLeft: 32, fontSize: 13, color: Colors.Gray600 }, children: [description && _jsx("div", { style: { marginBottom: 6 }, children: description }), clearEffect && (_jsxs("div", { style: { marginBottom: 8 }, children: [_jsx("strong", { children: "Clear action:" }), " ", clearEffect] })), children] })] }));
async function fetchJsonWithAuth(url, token) {
    try {
        const response = await fetch(url, {
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        if (!response.ok)
            return null;
        return await response.json();
    }
    catch {
        return null;
    }
}
export const ManageDataPanel = () => {
    const backendBaseUrl = React.useMemo(() => {
        return getResolvedPythonBackendUrl(PYTHON_BACKEND_URL).replace(/\/+$/, "");
    }, []);
    const [stats, setStats] = React.useState({
        pptLiveTracker: null,
        excelLiveTracker: null,
        pythonDecks: null,
        externalRag: null,
        plans: null,
        conversations: null,
    });
    const [clearOptions, setClearOptions] = React.useState({
        pptLiveTracker: false,
        excelLiveTracker: false,
        pythonDecks: false,
        externalRag: false,
        plans: false,
        conversations: false,
    });
    const [isLoading, setIsLoading] = React.useState(true);
    const [isClearing, setIsClearing] = React.useState(false);
    const [clearResults, setClearResults] = React.useState([]);
    const [error, setError] = React.useState(null);
    const fetchAllStats = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            await authService.initialize();
            const token = authService.getAccessToken();
            const [pptLiveTracker, excelLiveTracker, pythonStats, ragStats, plansStats, conversationsStats] = await Promise.all([
                fetchPptLiveTrackerStats(),
                fetchExcelLiveTrackerStats(),
                fetchJsonWithAuth(`${backendBaseUrl}/admin/stats`, token),
                fetchJsonWithAuth(`${backendBaseUrl}/rag/files`, token),
                fetchJsonWithAuth(`${backendBaseUrl}/admin/plans-stats`, token),
                fetchJsonWithAuth(`${backendBaseUrl}/admin/conversation-stats`, token),
            ]);
            setStats({
                pptLiveTracker,
                excelLiveTracker,
                pythonDecks: pythonStats
                    ? {
                        presentationCount: pythonStats.presentation_count || 0,
                        componentCount: pythonStats.component_count || 0,
                    }
                    : null,
                externalRag: ragStats
                    ? {
                        fileCount: ragStats.total_files || 0,
                        files: ragStats.files?.map((file) => file.filename) || [],
                    }
                    : null,
                plans: plansStats
                    ? {
                        planCount: plansStats.plan_count || 0,
                        totalSizeMb: plansStats.total_size_mb || 0,
                        byStatus: plansStats.by_status || {},
                    }
                    : null,
                conversations: conversationsStats
                    ? {
                        conversationCount: conversationsStats.conversation_count || 0,
                        totalSizeMb: conversationsStats.total_size_mb || 0,
                        byOrigin: conversationsStats.by_origin || {},
                    }
                    : null,
            });
        }
        catch (fetchError) {
            console.error("Error fetching manage data stats:", fetchError);
            setError("Failed to fetch database stats");
        }
        finally {
            setIsLoading(false);
        }
    }, [backendBaseUrl]);
    React.useEffect(() => {
        void fetchAllStats();
    }, [fetchAllStats]);
    const handleClose = React.useCallback(() => {
        try {
            Office.context.ui.messageParent(JSON.stringify({ status: "closed", action: "close" }));
        }
        catch {
            // Best effort only.
        }
    }, []);
    const selectedCount = React.useMemo(() => Object.values(clearOptions).filter(Boolean).length, [clearOptions]);
    const handleClear = React.useCallback(async () => {
        const selected = Object.entries(clearOptions)
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);
        if (selected.length === 0) {
            setError("Please select at least one data type to clear");
            return;
        }
        const selectedLabels = selected.map((key) => CLEAR_OPTION_LABELS[key]);
        const confirmMessage = [
            `You are about to clear ${selected.length} data store${selected.length === 1 ? "" : "s"}:`,
            ...selectedLabels.map((label) => `- ${label}`),
            "",
            "This action cannot be undone. Are you sure you want to continue?",
        ].join("\n");
        if (!window.confirm(confirmMessage)) {
            return;
        }
        setIsClearing(true);
        setClearResults([]);
        setError(null);
        const results = [];
        try {
            await authService.initialize();
            const token = authService.getAccessToken();
            const headers = {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            };
            if (clearOptions.pptLiveTracker) {
                await clearPptLiveTracker();
                results.push({ success: true, message: "PowerPoint live tracker cleared" });
            }
            if (clearOptions.excelLiveTracker) {
                await clearExcelLiveTracker();
                results.push({ success: true, message: "Excel live tracker cleared" });
            }
            if (clearOptions.pythonDecks) {
                const response = await fetch(`${backendBaseUrl}/admin/reset`, {
                    method: "POST",
                    headers,
                });
                if (response.ok) {
                    results.push({ success: true, message: "Presentation analysis data cleared" });
                }
                else {
                    results.push({ success: false, message: "Could not clear presentation analysis data" });
                }
            }
            if (clearOptions.externalRag) {
                const response = await fetch(`${backendBaseUrl}/rag/clear`, {
                    method: "POST",
                    headers,
                });
                if (response.ok) {
                    results.push({ success: true, message: "Uploaded file search data cleared" });
                }
                else {
                    results.push({ success: false, message: "Could not clear uploaded file search data" });
                }
            }
            if (clearOptions.plans) {
                const response = await fetch(`${backendBaseUrl}/admin/clear-plans`, {
                    method: "POST",
                    headers,
                });
                if (response.ok) {
                    results.push({ success: true, message: "Builder plans cleared" });
                }
                else {
                    results.push({ success: false, message: "Could not clear builder plans" });
                }
            }
            if (clearOptions.conversations) {
                const response = await fetch(`${backendBaseUrl}/admin/clear-conversations`, {
                    method: "POST",
                    headers,
                });
                if (response.ok) {
                    results.push({ success: true, message: "Saved conversations cleared" });
                }
                else {
                    results.push({ success: false, message: "Could not clear saved conversations" });
                }
            }
            setClearResults(results);
            await fetchAllStats();
            setClearOptions({
                pptLiveTracker: false,
                excelLiveTracker: false,
                pythonDecks: false,
                externalRag: false,
                plans: false,
                conversations: false,
            });
        }
        catch (clearError) {
            console.error("Error clearing selected data:", clearError);
            setError(`Error: ${clearError instanceof Error ? clearError.message : "Unknown error"}`);
        }
        finally {
            setIsClearing(false);
        }
    }, [backendBaseUrl, clearOptions, fetchAllStats]);
    return (_jsxs("div", { style: { height: "100%", minHeight: 0, backgroundColor: Colors.Gray100, display: "flex", flexDirection: "column" }, children: [_jsxs("header", { style: {
                    backgroundColor: Colors.MainBackground,
                    borderBottom: `1px solid ${Colors.Gray300}`,
                    padding: "12px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 12 }, children: [_jsx("div", { style: {
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    backgroundColor: Colors.PrimaryPurple10,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }, children: _jsx(DatabaseIcon, { className: "text-primary-purple-70" }) }), _jsxs("div", { children: [_jsx("h1", { style: { fontSize: 16, fontWeight: 700, color: Colors.Gray900, margin: 0 }, children: "Manage Data" }), _jsx("p", { style: { fontSize: 12, color: Colors.Gray600, margin: "2px 0 0 0" }, children: "Review local storage and clear data safely when needed." })] })] }), _jsx("button", { onClick: handleClose, type: "button", style: {
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            border: "none",
                            backgroundColor: Colors.Gray100,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background-color 0.15s ease",
                        }, onMouseOver: (event) => {
                            event.currentTarget.style.backgroundColor = Colors.Gray200;
                        }, onMouseOut: (event) => {
                            event.currentTarget.style.backgroundColor = Colors.Gray100;
                        }, children: _jsx(CloseIcon, {}) })] }), _jsxs("main", { style: {
                    padding: 24,
                    maxWidth: 700,
                    margin: "0 auto",
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 0,
                }, children: [_jsx("div", { style: {
                            marginBottom: 16,
                            padding: 12,
                            borderRadius: 10,
                            border: `1px solid ${Colors.PrimaryPurple20}`,
                            backgroundColor: Colors.PrimaryPurple05,
                            color: Colors.Gray700,
                            fontSize: 12,
                            lineHeight: 1.45,
                        }, children: "This page shows what data Carousel has saved. Clearing data can remove saved history and search/cache data, so some features may need to load or process again." }), isLoading ? (_jsxs("div", { style: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 60,
                            gap: 16,
                        }, children: [_jsx("div", { style: {
                                    width: 40,
                                    height: 40,
                                    border: `3px solid ${Colors.Gray200}`,
                                    borderTopColor: Colors.PrimaryPurple60,
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                } }), _jsx("p", { style: { color: Colors.Gray600, fontSize: 14 }, children: "Loading saved data summary..." }), _jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg); } }` })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }, children: [_jsx("h2", { style: { fontSize: 14, fontWeight: 600, color: Colors.Gray700, marginBottom: 4 }, children: "Choose what to clear" }), _jsx(DbCard, { title: "PowerPoint live tracker", checked: clearOptions.pptLiveTracker, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, pptLiveTracker: enabled }), description: "Stores slide snapshots and tracking state used by PowerPoint indexing.", clearEffect: "Removes PowerPoint local tracking state. Slides may be re-captured/re-indexed.", disabled: isClearing, children: stats.pptLiveTracker ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Snapshots: ", _jsx("strong", { children: stats.pptLiveTracker.snapshotCount })] }), _jsxs("div", { children: ["Decks: ", _jsx("strong", { children: stats.pptLiveTracker.deckCount })] }), _jsxs("div", { children: ["Approx. size: ", _jsx("strong", { children: formatFileSize(stats.pptLiveTracker.estimatedSize) })] })] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "No data" })) }), _jsx(DbCard, { title: "Excel live tracker", checked: clearOptions.excelLiveTracker, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, excelLiveTracker: enabled }), description: "Stores workbook-level tracking snapshots used by Excel indexing flows.", clearEffect: "Removes Excel local tracking state. Workbooks may be re-captured/re-indexed.", disabled: isClearing, children: stats.excelLiveTracker ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Workbooks: ", _jsx("strong", { children: stats.excelLiveTracker.workbookCount })] }), _jsxs("div", { children: ["Approx. size: ", _jsx("strong", { children: formatFileSize(stats.excelLiveTracker.estimatedSize) })] })] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "No data" })) }), _jsx(DbCard, { title: "Presentation analysis data", checked: clearOptions.pythonDecks, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, pythonDecks: enabled }), description: "Stores analyzed slide content, layouts, and search data for faster results.", clearEffect: "Clears analyzed presentation data. Slides will be analyzed again when needed.", disabled: isClearing, children: stats.pythonDecks ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Presentations: ", _jsx("strong", { children: stats.pythonDecks.presentationCount })] }), _jsxs("div", { children: ["Components: ", _jsx("strong", { children: stats.pythonDecks.componentCount })] })] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "Unable to fetch stats" })) }), _jsx(DbCard, { title: "Uploaded file search data", checked: clearOptions.externalRag, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, externalRag: enabled }), description: "Stores searchable data from files you uploaded (PDF, Word, Excel).", clearEffect: "Clears searchable file data. Files may need to be uploaded/indexed again.", disabled: isClearing, children: stats.externalRag ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Files: ", _jsx("strong", { children: stats.externalRag.fileCount })] }), stats.externalRag.files.length > 0 && (_jsxs("div", { style: { marginTop: 4, fontSize: 12 }, children: [stats.externalRag.files.slice(0, 3).map((filename, index) => (_jsxs("div", { style: { color: Colors.Gray500 }, children: ["- ", filename] }, index))), stats.externalRag.files.length > 3 && (_jsxs("div", { style: { color: Colors.Gray500 }, children: ["... and ", stats.externalRag.files.length - 3, " more"] }))] }))] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "Unable to fetch stats" })) }), _jsx(DbCard, { title: "Builder plans", checked: clearOptions.plans, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, plans: enabled }), description: "Stores saved deck-builder plans and in-progress build state.", clearEffect: "Deletes saved plans and build checkpoints. In-progress plan history is removed.", disabled: isClearing, children: stats.plans ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Plans: ", _jsx("strong", { children: stats.plans.planCount })] }), stats.plans.totalSizeMb > 0 && (_jsxs("div", { children: ["Size: ", _jsxs("strong", { children: [stats.plans.totalSizeMb, " MB"] })] })), Object.keys(stats.plans.byStatus).length > 0 && (_jsx("div", { style: { marginTop: 4, fontSize: 12 }, children: Object.entries(stats.plans.byStatus).map(([status, count]) => (_jsxs("div", { style: { color: Colors.Gray500 }, children: ["- ", status, ": ", count] }, status))) }))] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "Unable to fetch stats" })) }), _jsx(DbCard, { title: "Saved conversations", checked: clearOptions.conversations, onCheckedChange: (enabled) => setClearOptions({ ...clearOptions, conversations: enabled }), description: "Stores chat history saved on disk for restore/resume.", clearEffect: "Deletes saved conversation history. Active chats in this session are not directly changed.", disabled: isClearing, children: stats.conversations ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { children: ["Conversations: ", _jsx("strong", { children: stats.conversations.conversationCount })] }), stats.conversations.totalSizeMb > 0 && (_jsxs("div", { children: ["Size: ", _jsxs("strong", { children: [stats.conversations.totalSizeMb, " MB"] })] })), Object.keys(stats.conversations.byOrigin).length > 0 && (_jsx("div", { style: { marginTop: 4, fontSize: 12 }, children: Object.entries(stats.conversations.byOrigin).map(([origin, count]) => (_jsxs("div", { style: { color: Colors.Gray500 }, children: ["- ", origin === "mainTaskpane" ? "Taskpane" : origin === "builder" ? "Builder" : origin, ": ", count] }, origin))) }))] })) : (_jsx("span", { style: { fontStyle: "italic", color: Colors.Gray500 }, children: "Unable to fetch stats" })) })] }), _jsxs("div", { style: { display: "flex", gap: 12 }, children: [_jsxs("button", { onClick: () => void handleClear(), disabled: isClearing || selectedCount === 0, style: {
                                            flex: 1,
                                            padding: "12px 20px",
                                            borderRadius: 8,
                                            border: "none",
                                            backgroundColor: selectedCount > 0 ? Colors.Red60 : Colors.Gray300,
                                            color: selectedCount > 0 ? "white" : Colors.Gray500,
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: selectedCount > 0 && !isClearing ? "pointer" : "not-allowed",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 8,
                                            opacity: isClearing ? 0.7 : 1,
                                            transition: "all 0.15s ease",
                                        }, children: [_jsx(TrashIcon, {}), isClearing ? "Clearing..." : `Clear Selected (${selectedCount})`] }), _jsxs("button", { onClick: () => void fetchAllStats(), disabled: isClearing, style: {
                                            padding: "12px 16px",
                                            borderRadius: 8,
                                            border: `1px solid ${Colors.Gray300}`,
                                            backgroundColor: Colors.MainBackground,
                                            color: Colors.Gray700,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            cursor: isClearing ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            transition: "all 0.15s ease",
                                        }, children: [_jsx(RefreshIcon, {}), "Refresh"] })] }), clearResults.length > 0 && (_jsxs("div", { style: {
                                    marginTop: 20,
                                    padding: 16,
                                    backgroundColor: Colors.MainBackground,
                                    border: `1px solid ${Colors.Gray300}`,
                                    borderRadius: 10,
                                    fontSize: 13,
                                }, children: [_jsx("div", { style: { fontWeight: 600, color: Colors.Gray800, marginBottom: 8 }, children: "Clear results" }), _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: clearResults.map((entry, index) => (_jsxs("div", { style: {
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                color: entry.success ? Colors.Green80 : Colors.Red80,
                                            }, children: [entry.success ? _jsx(CheckIcon, {}) : _jsx(XCircleIcon, {}), _jsx("span", { children: entry.message })] }, `${entry.message}-${index}`))) })] })), error && (_jsx("div", { style: {
                                    marginTop: 20,
                                    padding: 16,
                                    backgroundColor: Colors.Red10,
                                    border: `1px solid ${Colors.Red30}`,
                                    borderRadius: 10,
                                    fontSize: 13,
                                    color: Colors.Red80,
                                }, children: error }))] }))] })] }));
};
export default ManageDataPanel;
